import React, { createContext, useState, useContext, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => useContext(CurrencyContext);

export const CurrencyProvider = ({ children }) => {
    // Initialize currency from localStorage or default to 'USD'
    // Changed key to 'bailebelle_currency_v2' to force re-detection for all users
    const [currency, setCurrency] = useState(() => {
        const storedCurrency = localStorage.getItem('bailebelle_currency_v2');
        return storedCurrency || 'USD';
    });
    const [exchangeRate, setExchangeRate] = useState(278); // Default fallback rate
    const [loading, setLoading] = useState(true);

    // Detect user's country and set currency on first visit
    useEffect(() => {
        const detectAndSetCurrency = async () => {
            // Check if currency preference already exists
            const storedCurrency = localStorage.getItem('bailebelle_currency_v2');

            if (!storedCurrency) {
                console.log('No stored currency preference found. Initiating location detection for currency...');
                // No stored preference, detect location
                let detectedCurrency = 'USD'; // Default fallback
                let countryCode = null;

                // Helper function to fetch with timeout
                const fetchWithTimeout = (url, options = {}, timeout = 5000) => {
                    return Promise.race([
                        fetch(url, options),
                        new Promise((_, reject) =>
                            setTimeout(() => reject(new Error('Timeout')), timeout)
                        )
                    ]);
                };

                // Strategy: Try multiple IP geolocation services
                // 1. geojs.io (Simple, reliable, CORS-friendly)
                try {
                    const response = await fetchWithTimeout('https://get.geojs.io/v1/ip/geo.json', {
                        method: 'GET',
                    }, 4000);

                    if (response.ok) {
                        const data = await response.json();
                        if (data && data.country_code) { // geojs uses country_code
                            countryCode = data.country_code;
                            detectedCurrency = countryCode === 'PK' ? 'PKR' : 'USD';
                            console.log(`Currency detected via geojs.io: ${detectedCurrency} (Country: ${countryCode})`);
                        }
                    }
                } catch (error) {
                    console.log('geojs.io failed, trying alternative...', error.message);
                }

                // 2. ipapi.co (Good, but sometimes has rate limits or CAPTCHAs)
                if (!countryCode) {
                    try {
                        const response = await fetchWithTimeout('https://ipapi.co/json/', {
                            method: 'GET',
                            headers: { 'Accept': 'application/json' },
                        }, 4000);

                        // efficient check for JSON content type
                        const contentType = response.headers.get("content-type");
                        if (response.ok && contentType && contentType.indexOf("application/json") !== -1) {
                            const data = await response.json();
                            if (data && data.country_code) {
                                countryCode = data.country_code;
                                detectedCurrency = countryCode === 'PK' ? 'PKR' : 'USD';
                                console.log(`Currency detected via ipapi.co: ${detectedCurrency} (Country: ${countryCode})`);
                            }
                        }
                    } catch (error) {
                        console.log('ipapi.co failed, trying alternative...', error.message);
                    }
                }

                // 3. ipinfo.io (Reliable fallback)
                if (!countryCode) {
                    try {
                        const response = await fetchWithTimeout('https://ipinfo.io/json', {
                            method: 'GET',
                            headers: { 'Accept': 'application/json' },
                        }, 4000);

                        if (response.ok) {
                            const data = await response.json();
                            if (data && data.country) {
                                countryCode = data.country;
                                detectedCurrency = countryCode === 'PK' ? 'PKR' : 'USD';
                                console.log(`Currency detected via ipinfo.io: ${detectedCurrency} (Country: ${countryCode})`);
                            }
                        }
                    } catch (error) {
                        console.log('ipinfo.io failed, using default currency', error.message);
                    }
                }

                // Set the detected currency
                setCurrency(detectedCurrency);
                localStorage.setItem('bailebelle_currency_v2', detectedCurrency);
                if (countryCode) {
                    console.log(`Currency auto-detected: ${detectedCurrency} (Country: ${countryCode})`);
                } else {
                    console.log(`Using default currency: ${detectedCurrency} (Location detection unavailable)`);
                }
            } else {
                // Currency already exists in localStorage, just set loading to false
                setLoading(false);
            }
        };

        detectAndSetCurrency();
    }, []);

    useEffect(() => {
        const fetchExchangeRate = async () => {
            try {
                // Using a free API for exchange rates with timeout
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000);

                const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD', {
                    signal: controller.signal
                });
                clearTimeout(timeoutId);

                if (response.ok) {
                    const data = await response.json();
                    if (data && data.rates && data.rates.PKR) {
                        setExchangeRate(data.rates.PKR);
                        console.log(`Exchange rate updated: 1 USD = ${data.rates.PKR} PKR`);
                    }
                } else {
                    console.warn('Exchange rate API response not ok, using fallback rate');
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Failed to fetch exchange rate:', error);
                }
                console.log('Using fallback exchange rate: 278 PKR per USD');
            } finally {
                setLoading(false);
            }
        };

        fetchExchangeRate();
    }, []);

    const formatPrice = (amountInUSD) => {
        if (currency === 'USD') {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }).format(amountInUSD);
        } else {
            const amountInPKR = amountInUSD * exchangeRate;
            return new Intl.NumberFormat('en-PK', {
                style: 'currency',
                currency: 'PKR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amountInPKR);
        }
    };

    const convertPrice = (amountInUSD) => {
        if (currency === 'USD') return amountInUSD;
        return amountInUSD * exchangeRate;
    };

    // Wrap setCurrency to also save to localStorage
    const handleSetCurrency = (newCurrency) => {
        setCurrency(newCurrency);
        localStorage.setItem('bailebelle_currency_v2', newCurrency);
    };

    const value = {
        currency,
        setCurrency: handleSetCurrency,
        exchangeRate,
        formatPrice,
        convertPrice,
        loading
    };

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
};
